/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  chartLegendCellBar,
  chartLegendCellDashedLine,
  chartLegendCellNone,
  chartLegendRow,
  ChartSpecsWithLegend,
} from '@gh/builder-shared-charts';
import {
  DashboardChartType,
  GrossAndNetProfitDto,
  GrossAndNetProfitPoint,
  GrossAndNetProfitValue,
} from '@gh/builder-shared-data';
import { fill, head } from 'lodash';

export type GrossAndNetProfit = GrossAndNetProfitDto;
export const grossAndNetProfitFromDto = (dto: GrossAndNetProfitDto, isCorporate: boolean): ChartSpecsWithLegend => {
  const minBars = 9;
  const emptyValue: GrossAndNetProfitValue = {
    xAxisValue: '',
    points: [
      {
        type: DashboardChartType.GrossProfit,
        yAxisValue: undefined,
      },
      {
        type: DashboardChartType.NetProfit,
        yAxisValue: undefined,
      },
    ],
  };

  const dataTable: any[][] = [];
  dataTable.push([
    { type: 'string', label: '' },
  ]);

  // Add hidden columns for chart bars width corrections
  if (dto.values.length < minBars) {
    const diff = Math.floor((minBars - dto.values.length) / 2);

    dto.values.unshift(...fill(Array(diff), emptyValue));
    dto.values.push(...fill(Array(diff), emptyValue));
  }

  dto.values.unshift(emptyValue);
  dto.values.push(emptyValue);

  head(dataTable)!.push(...[
    { type: 'number', label: 'lbl_gross_profit_pct' },
    { type: 'string', role: 'tooltip', p: { html: true } },
    { type: 'number', label: 'lbl_net_profit_pct' },
    { type: 'string', role: 'tooltip', p: { html: true } },
    { type: 'number', label: isCorporate ? 'lbl_avg_gross_profit' : 'lbl_avg_gross_profit_master_area' },
    { type: 'string', role: 'tooltip', p: { html: true } },
    { type: 'number', label: isCorporate ? 'lbl_avg_net_profit' : 'lbl_avg_net_profit_master_area' },
    { type: 'string', role: 'tooltip', p: { html: true } },
  ]);

  dto.values.forEach((value: GrossAndNetProfitValue) => {
    const rowValues: any[] = [value.xAxisValue];
    value.points.forEach((point: GrossAndNetProfitPoint) => {
      rowValues.push(...[point.yAxisValue || 0, {
        label: DashboardChartType.GrossProfit === point.type ? 'lbl_gross_profit_pct_cnt' : 'lbl_net_profit_pct_cnt',
        value: { count: point.yAxisValue },
      }]);
    });

    rowValues.push(...[
      dto.averageGrossProfit,
      {
        label: isCorporate ? 'lbl_avg_gross_profit_cnt' : 'lbl_avg_gross_profit_master_area_cnt',
        value: { count: dto.averageGrossProfit },
      },
      dto.averageNetProfit,
      {
        label: isCorporate ? 'lbl_avg_net_profit_cnt' : 'lbl_avg_net_profit_master_area_cnt',
        value: { count: dto.averageNetProfit },
      },
    ]);
    dataTable.push(rowValues);
  });

  return {
    legendLayout: [
      chartLegendRow(
        chartLegendCellBar('lbl_gross_profit_pct', '#009ce7'),
        chartLegendCellBar('lbl_net_profit_pct', '#e500ff'),
        chartLegendCellDashedLine(
          isCorporate ? 'lbl_avg_gross_profit' : 'lbl_avg_gross_profit_master_area',
          '#00ca00')),
      chartLegendRow(
        chartLegendCellNone(),
        chartLegendCellNone(),
        chartLegendCellDashedLine(
          isCorporate ? 'lbl_avg_net_profit' : 'lbl_avg_net_profit_master_area',
          '#ff0000')),
    ],
    chartType: 'ComboChart',
    dataTable,
    options: {
      seriesType: 'bars',
      colors: ['#009ce7', '#e500ff'],
      series: {
        2: {
          type: 'line',
          lineDashStyle: [4, 1],
          pointsVisible: false,
          color: '#00ca00',
        },
        3: {
          type: 'line',
          lineDashStyle: [4, 1],
          pointsVisible: false,
          color: '#ff0000',
        },
      },
    },
  };
};
